import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { allProducts } from '../../mocks/products';

interface CartItem {
  id: number;
  name: string;
  price: number;
  image: string;
  category: string;
  specs: string;
  quantity: number;
}

export default function CartPage() {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);

  useEffect(() => {
    loadCart();
    
    const handleCartUpdate = () => {
      loadCart();
    };
    
    window.addEventListener('cartUpdated', handleCartUpdate);
    return () => window.removeEventListener('cartUpdated', handleCartUpdate);
  }, []);

  const loadCart = () => {
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    setCartItems(cart);
  };

  const updateQuantity = (id: number, newQuantity: number) => {
    if (newQuantity < 1) return;
    
    const updatedCart = cartItems.map(item =>
      item.id === id ? { ...item, quantity: newQuantity } : item
    );
    
    setCartItems(updatedCart);
    localStorage.setItem('cart', JSON.stringify(updatedCart));
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const removeItem = (id: number) => {
    const updatedCart = cartItems.filter(item => item.id !== id);
    setCartItems(updatedCart);
    localStorage.setItem('cart', JSON.stringify(updatedCart));
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const shipping = subtotal > 50 ? 0 : 7.99;
  const tax = subtotal * 0.08;
  const total = subtotal + shipping + tax;

  const suggestedProducts = allProducts.filter(p => !cartItems.find(item => item.id === p.id)).slice(0, 4);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-5xl font-black text-gray-900 mb-12">Shopping Cart</h1>

          {cartItems.length === 0 ? (
            <div className="text-center py-20">
              <i className="ri-shopping-cart-line text-8xl text-gray-300 mb-6"></i>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Your cart is empty</h2>
              <p className="text-lg text-gray-600 mb-8">Add some awesome products to get started!</p>
              <Link
                to="/products"
                className="inline-block px-10 py-4 bg-black text-white font-semibold rounded-xl hover:bg-[#FF6B6B] transition-colors whitespace-nowrap cursor-pointer"
              >
                Browse Products
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="col-span-2 space-y-4">
                {cartItems.map((item) => (
                  <div key={item.id} className="bg-white border border-gray-200 rounded-2xl p-6 flex gap-6">
                    <div className="w-32 h-32 flex-shrink-0 bg-gray-50 rounded-xl overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex justify-between mb-2">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">{item.name}</h3>
                          <p className="text-sm text-gray-600">{item.specs}</p>
                          <p className="text-xs text-gray-500 mt-1">{item.category}</p>
                        </div>
                        <button
                          onClick={() => removeItem(item.id)}
                          className="text-red-500 hover:text-red-700 text-sm font-medium cursor-pointer whitespace-nowrap"
                        >
                          Remove
                        </button>
                      </div>
                      
                      <div className="flex items-center justify-between mt-4">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer"
                          >
                            <i className="ri-subtract-line"></i>
                          </button>
                          <span className="w-12 text-center font-semibold">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer"
                          >
                            <i className="ri-add-line"></i>
                          </button>
                        </div>
                        
                        <div className="text-2xl font-black text-[#FF6B6B]">
                          ${(item.price * item.quantity).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Order Summary */}
              <div className="col-span-1">
                <div className="bg-white border border-gray-200 rounded-2xl p-8 sticky top-24">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Order Summary</h2>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between text-gray-700">
                      <span>Subtotal</span>
                      <span className="font-semibold">${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-gray-700">
                      <span>Shipping</span>
                      <span className="font-semibold">
                        {shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}
                      </span>
                    </div>
                    <div className="flex justify-between text-gray-700">
                      <span>Tax</span>
                      <span className="font-semibold">${tax.toFixed(2)}</span>
                    </div>
                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex justify-between">
                        <span className="text-xl font-bold text-gray-900">Total</span>
                        <span className="text-3xl font-black text-[#FF6B6B]">
                          ${total.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {subtotal < 50 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                      <p className="text-sm text-blue-800">
                        Add <strong>${(50 - subtotal).toFixed(2)}</strong> more to get free shipping!
                      </p>
                    </div>
                  )}
                  
                  <button 
                    onClick={() => navigate('/checkout')}
                    className="w-full h-14 bg-black text-white font-bold rounded-xl hover:bg-[#FF6B6B] transition-colors mb-4 whitespace-nowrap cursor-pointer"
                  >
                    Proceed to Checkout
                  </button>
                  
                  <Link
                    to="/products"
                    className="block text-center text-sm text-gray-600 hover:text-[#FF6B6B] transition-colors cursor-pointer"
                  >
                    Continue Shopping
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* You May Also Like */}
          {cartItems.length > 0 && suggestedProducts.length > 0 && (
            <section className="mt-20">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">You May Also Like</h2>
              <div className="grid grid-cols-4 gap-6" data-product-shop>
                {suggestedProducts.map((product) => (
                  <div
                    key={product.id}
                    className="bg-white rounded-2xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-300 group"
                  >
                    <div className="aspect-square bg-gray-50 overflow-hidden">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-bold text-gray-900 mb-2">{product.name}</h3>
                      <div className="flex items-center justify-between">
                        <span className="text-xl font-black text-[#FF6B6B]">
                          ${product.price}
                        </span>
                        <button
                          onClick={() => {
                            const cart = JSON.parse(localStorage.getItem('cart') || '[]');
                            cart.push({ ...product, quantity: 1 });
                            localStorage.setItem('cart', JSON.stringify(cart));
                            window.dispatchEvent(new Event('cartUpdated'));
                            loadCart();
                          }}
                          className="px-4 py-2 bg-black text-white text-xs font-semibold rounded-lg hover:bg-[#FF6B6B] transition-colors whitespace-nowrap cursor-pointer"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
